import { datadogRum } from "@datadog/browser-rum";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import TagManager from "react-gtm-module";

import { getDataFromSessionStorage, setDataInSessionStorage } from "./helper";
import { useLogger } from "./logger";

export const useAdpSearch = ({
  category,
  journey,
  personalData,
  addressData,
  gtmId,
  gtmEvent,
}) => {
  const dDogLogger = useLogger();
  const [search, setSearch] = useState(false);
  const [searchData, setSearchData] = useState(null);
  const [adpSearchCompletedSuccessfully, setAdpSearchCompleted] =
    useState(false);

  const router = useRouter();

  const handleRejectionOrFail = useCallback(() => {
    const query = JSON.stringify({
      mobile_phone: personalData.mobileNumbers,
      email_address: personalData.emailAddresses,
      salutation: personalData.title,
      first_name: personalData.firstName,
      last_name: personalData.lastName,
      birth_date: personalData.dateOfBirth,
      postcode: addressData.postcode,
      street: addressData.street,
      town_or_city: addressData.town,
      buildingNumber: addressData.buildingNumber,
      buildingName: addressData.buildingName,
    });
    const tagManagerArgs = {
      gtmId,
      dataLayer: {
        event: gtmEvent,
      },
    };
    TagManager.initialize(tagManagerArgs);
    router.push(
      `/${journey}/declined?data=${query}`,
      `/${journey}/declined?nd=y`,
    );
  }, [
    personalData.mobileNumbers,
    personalData.emailAddresses,
    personalData.title,
    personalData.firstName,
    personalData.lastName,
    personalData.dateOfBirth,
    addressData.postcode,
    addressData.street,
    addressData.town,
    addressData.buildingNumber,
    addressData.buildingName,
    router,
    journey,
  ]);

  useEffect(() => {
    if (!search) return;

    if (!getDataFromSessionStorage("applicationTokens")) {
      dDogLogger.error(`ADPError:Missing applicationTokens`);
      return;
    }

    let onmouuid = getDataFromSessionStorage("applicationTokens").uuid;
    let accessToken = getDataFromSessionStorage("applicationTokens").onmoAT;

    if (!onmouuid || !accessToken) {
      dDogLogger.error(`ADPError:Missing onmouuid or accessToken`);
      return;
    }

    const nextPage = page => router.push(`/${journey}/${page}`);

    const searchAPIInterval = setInterval(async () => {
      const searchBody = {
        onmouuid,
        category,
      };

      try {
        const searchResponse = await fetch("/api/partner/search", {
          method: "POST",
          headers: { Authorization: `${accessToken}` },
          body: JSON.stringify(searchBody),
        });

        const searchRes = await searchResponse.json();

        if (!searchResponse.ok || !searchRes.data) {
          clearInterval(searchAPIInterval);
          dDogLogger.error(`ADPSearchError`);
          nextPage("error");
        }

        const { adpErrorMessage } = searchRes.data;
        const searchData = searchRes.data;

        if (searchData.newOnmoUUID && searchData.token) {
          datadogRum.setUserProperty("newOnmoUUID", searchData.newOnmoUUID);
          datadogRum.addAction("6.1.1-Existing-Customer-Found", {
            onboardingChannel: "Organic",
          });
          dDogLogger.log(
            `Existing Customer found with uuid: ${searchData.newOnmoUUID}`,
          );
          const tokens = {
            ...(getDataFromSessionStorage("applicationTokens") || {}),
            uuid: searchData.token.onmouuid,
            onmoAT: searchData.token.accessToken,
            backendkey: searchData.token.backendkey,
          };
          setDataInSessionStorage("applicationTokens", tokens);
          onmouuid = searchData.token.onmouuid;
          accessToken = searchData.token.accessToken;
        }

        if (searchRes.data.adpStatus === "Complete") {
          clearInterval(searchAPIInterval);

          dDogLogger.warn(`adpStatus:Complete`);

          if (searchRes.data.approved) {
            if (searchRes.data.OBrequired) {
              dDogLogger.log(`adpStatus:Complete OBrequired`);
              nextPage("open-banking");
            } else {
              setSearchData(searchRes.data);
              dDogLogger.log(`adpStatus:Complete accepted`);
              setAdpSearchCompleted(true);
            }
          } else {
            dDogLogger.warn(`ADPError:${adpErrorMessage}`);
            handleRejectionOrFail();
          }
        } else if (searchRes.data.adpStatus === "Fail") {
          clearInterval(searchAPIInterval);
          dDogLogger.warn(`adpStatus:Fail ADPError:${adpErrorMessage}`);
          handleRejectionOrFail();
        }
      } catch (err) {
        clearInterval(searchAPIInterval);
        dDogLogger.error(`ADPError:${JSON.stringify(err)}`);
        handleRejectionOrFail();
      }
    }, 5000);
  }, [search]);

  return { setSearch, searchData, adpSearchCompletedSuccessfully, search };
};
